import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { ROLES, SCOPES } from '../../../config/permissions/roles';
import { isAwardType } from '../../../helpers/awardTypes';
import { combine } from '../../../helpers/styles';
import { useJuror } from '../../../hooks/useAuth';
import { AWARD_TYPES } from '../../../models/award';
import { SESSION_TYPES } from '../../../models/session';
import Submission from '../../../models/submission';
import PermissionHandler from '../../../modules/permissionHandler';
import ActionsColumn from '../../entriesTable/actionsColumn';
import EvaluationCategory from './evaluationCategory';
import styles from './styles.module.scss';
import { useConfig } from '../../../hooks/useConfig';

const JurorEvaluation = ({
  submission,
  updateSubmissions,
  onEvaluationChange,
  isReadOnly,
}) => {
  const { jurorId: impersonatingJurorId } = useParams();
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [scoreDifference, setScoreDifference] = useState(0);
  const hasEvaluationChanged = useRef(false);
  const { jurorGroup, role, session } = useJuror();
  const { t } = useTranslation();
  const { state } = useLocation();
  const { EVALUATION_CRITERIA_ORDER } = useConfig();

  const maxPoints = submission?.maxPointsPerCriterion || '50';

  const score = useMemo(() => {
    if (!submission || submission.points.length === 0) return 0;

    return submission.points.reduce(
      (sum, criterion) =>
        criterion.points === null ? sum : sum + criterion.points,
      0
    );
  }, [submission]);

  // TODO: Consider doing a refactor, that moves the calculation to the corresponding model.
  const preselectionScore = useMemo(() => {
    if (!submission || !submission.scoresFromPriorSessions.length) return '-';

    const totalEvaluationPoints =
      submission.scoresFromPriorSessions[0]?.totalEvaluationPoints;
    const numDecidedEvaluations =
      submission.scoresFromPriorSessions[0]?.numDecidedEvaluations;

    if (
      isNaN(totalEvaluationPoints) ||
      isNaN(numDecidedEvaluations) ||
      numDecidedEvaluations === 0
    ) {
      return '-';
    }

    return Math.floor(totalEvaluationPoints / numDecidedEvaluations);
  }, [submission]);

  const lastSessionAverage = useMemo(() => {
    if (!submission?.scoreFromPriorSessions?.length) {
      return 0;
    }

    const lastSession =
      submission.scoresFromPriorSessions[
        submission.scoresFromPriorSessions.length - 1
      ];

    return Math.round(
      lastSession.totalEvaluationPoints / lastSession.numDecidedEvaluations
    );
  }, []);

  useEffect(() => {
    if (hasEvaluationChanged.current) {
      setScoreDifference(
        submission.points.every((criteria) => criteria.points !== null)
          ? lastSessionAverage - score
          : 0
      );
    }
  }, [score, lastSessionAverage]);

  const sortedCategories = useMemo(() => {
    return [...submission.points].sort(
      (a, b) =>
        EVALUATION_CRITERIA_ORDER.indexOf(a.criterion.name.toLowerCase()) -
        EVALUATION_CRITERIA_ORDER.indexOf(b.criterion.name.toLowerCase())
    );
  }, [submission]);

  const handleEvaluationChange = (points, criterionId) => {
    const pointsInt = parseInt(points);
    hasEvaluationChanged.current = true;

    onEvaluationChange(pointsInt, criterionId);
    debounceChangeHandler(pointsInt, criterionId);
  };

  const updateEvaluation = (points, criterionId) => {
    Submission.updateEvaluation(
      state?.jurorGroupId || jurorGroup.jurorGroupId,
      submission.submissionId,
      criterionId,
      points,
      impersonatingJurorId
    );
  };

  const debounceChangeHandler = useCallback(
    debounce(updateEvaluation, 300),
    []
  );

  const getScoreFromPriorSessionByCriterion = (criterion) => {
    const criterionFromPriorSession =
      submission?.scoresFromPriorSessions?.[0]?.criteria?.find(
        (criteria) => criteria.name === criterion
      );

    const numDecidedEvaluations =
      submission?.scoresFromPriorSessions?.[0]?.numDecidedEvaluations;

    if (
      !criterionFromPriorSession ||
      isNaN(criterionFromPriorSession.totalPoints) ||
      isNaN(numDecidedEvaluations) ||
      numDecidedEvaluations === 0
    )
      return null;

    return Math.floor(
      criterionFromPriorSession.totalPoints / numDecidedEvaluations
    );
  };

  return (
    <div className={styles.evaluationWrapper}>
      {scoreDifference > session.scoreAlertCThreshold && (
        <Alert
          variant="danger"
          dismissible
          className={styles.scoreAlert}
          onClose={() => setScoreDifference(0)}
        >
          {t('evaluation.priorSessionScoreAlert', {
            amount: scoreDifference,
          })}
        </Alert>
      )}
      <Container>
        <Row>
          <Col className={styles.titleWrapper}>
            <h2 className={styles.evaluationTitle}>{t('evaluation.title')}</h2>

            <div className={styles.rightColum}>
              <ActionsColumn
                submission={submission}
                updateSubmission={updateSubmissions}
                isReadOnly={isReadOnly}
                isDetailPage
              />
              {isAwardType(AWARD_TYPES.IF_DA, session) &&
                session?.sessionType === SESSION_TYPES.FINAL_JURY && (
                  <div
                    className={combine(styles.totalScore, styles.preselection)}
                  >
                    <span className={styles.label}>
                      {t('evaluation.preselection')}
                    </span>
                    <span className={styles.score}>{preselectionScore}</span>
                  </div>
                )}
              <div className={styles.totalScore}>
                <span className={styles.label}>
                  {t('evaluation.totalScore')}
                </span>
                <span className={styles.score}>{score}</span>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <PermissionHandler
        scopes={[SCOPES.canEvaluateSubmission]}
        errorProps={{ style: { pointerEvents: 'none' } }}
      >
        <ul
          className={combine(
            styles.evaluationList,
            submission.chairPersonMustDecide &&
              role != ROLES.chairPerson &&
              styles.disabled,
            !submission.chairPersonMustDecide &&
              role == ROLES.chairPerson &&
              styles.disabled,
            role == ROLES.staff && styles.disabled,
            session?.prizeMoneyModeEnabled && styles.disabled,
            isReadOnly && styles.disabled
          )}
        >
          {sortedCategories.map((item, index) => (
            <EvaluationCategory
              key={item.criterion.id}
              category={item}
              text={
                submission?.fields[`${item.criterion.name.toLowerCase()}`] ||
                item.criterion.description ||
                ''
              }
              isActive={
                index === selectedCategory ||
                (!submission.chairPersonMustDecide && role == ROLES.chairPerson)
              }
              maxPoints={maxPoints}
              onCategoryClick={(setActive) => {
                setSelectedCategory(setActive ? index : -1);
              }}
              handleEvaluationChange={handleEvaluationChange}
              pointsStep={submission?.pointsStep}
              scoreFromPriorSession={getScoreFromPriorSessionByCriterion(
                item?.criterion?.name
              )}
            />
          ))}
        </ul>
      </PermissionHandler>
    </div>
  );
};

JurorEvaluation.propTypes = {
  submission: PropTypes.object,
  onEvaluationChange: PropTypes.func,
  updateSubmissions: PropTypes.func,
  isReadOnly: PropTypes.bool,
};

JurorEvaluation.defaultProps = {
  submission: null,
  onEvaluationChange: () => null,
  updateSubmissions: () => null,
  isReadOnly: false,
};

export default JurorEvaluation;
